/**
 * City outfits (launch plan Phase 1 step 6): on a city's journey segment and in its levels, Pip
 * wears that city's own outfit (`city_<city>`, @mp/art pip-city.ts) unless the player equipped a
 * wardrobe outfit: an explicit choice always wins, and only the classic look (no outfit) wears the
 * city's. Settings › City outfits (on by default) turns them off. A city has an outfit when its
 * pack brought the overlay (the atlas group `costume_<city>`): New York, Tokyo, Paris, an event
 * and a pack built before outfits keep Pip classic. Share cards and the wardrobe always show the
 * equipped outfit, never this one.
 */
import type { Settings } from './state.js';

/** The atlas group a city pack carries its outfit in (@mp/art `pipCityCostumeGroup`). */
export const cityOutfitGroup = (city: string): string => `costume_${city}`;

/** A city outfit's costume id (its overlay frame is `chr_pip_cos_<id>`). */
export const cityOutfitId = (city: string): string => `city_${city}`;

/** The atlas group an outfit's overlay is in: a city outfit's pack group, else the wardrobe's `pip_extra`. */
export const outfitGroup = (costume: string): string => (costume.startsWith('city_') ? cityOutfitGroup(costume.slice('city_'.length)) : 'pip_extra');

/** Whether the city's pack brought Pip's outfit (its group is in the art library's index). */
export const hasCityOutfit = (art: { groupsMatching(patterns: readonly string[]): string[] }, city: string): boolean => art.groupsMatching([cityOutfitGroup(city)]).length > 0;

/**
 * What Pip wears in `city`: the equipped wardrobe outfit, else the city's outfit when it has one
 * and City outfits is on, else classic. `hasOutfit` says whether the city's pack carries one
 * (the scenes ask the art library: `hasCityOutfit`).
 */
export function outfitFor(s: { costume: string; settings: Pick<Settings, 'cityOutfits'> }, city: string, hasOutfit: (city: string) => boolean): string {
  if (s.costume !== 'classic') return s.costume;
  if (s.settings.cityOutfits === false || !hasOutfit(city)) return 'classic';
  return cityOutfitId(city);
}
